import { defaults as defaultControls, ScaleLine } from 'ol/control';
import Feature from 'ol/Feature';
import { defaults as defaultInteractions, MouseWheelZoom } from 'ol/interaction';
import Point from 'ol/geom/Point';
import type Geometry from 'ol/geom/Geometry';
import type Polygon from 'ol/geom/Polygon';
import type SimpleGeometry from 'ol/geom/SimpleGeometry';
import Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import type { FeatureLike } from 'ol/Feature';
import type RenderEvent from 'ol/render/Event';
import { getVectorContext } from 'ol/render';
import { Fill, Stroke, Style } from 'ol/style';
import type { AppStore } from '../core/store';
import type { AppState, BasemapId, HybridReferenceVisibility, LayerModel, Selection } from '../core/types';
import { buildLabelFeatures } from '../domain/labels';
import { labelTargetFragmentIds } from '../domain/labelTarget';
import { layerClassificationAssignments } from '../domain/classification';
import { secondaryTheme } from '../presentation/dualTheme';
import { exceptionSubareaIds, masterThemeAssignments } from '../presentation/masterTheme';
import { deriveDisplaySet } from '../domain/displaySet';
import { visibleThemeTargetIds } from '../domain/themeTarget';
import { selectedFragmentIds } from '../domain/selection';
import { selectedFragmentIdsForSet } from '../domain/selectionSet';
import { entityViewFragmentIds } from '../domain/entityView';
import { basemapLayers, refreshBasemapLayers, setHybridReferenceVisibility, setVisibleBasemap } from './basemaps';
import { buildEntityPresentationFeatures } from './presentationGeometry';
import { PrecisionWheelZoom, PrecisionZoomControl, shouldUseNativeWheelZoom } from './precisionZoom';
import { entityPresentationStyle, fragmentStyle, genericVectorStyle, labelStyle } from './styles';
import { casurHydricStyle } from './casurHydricStyle';
import { planningLayerStyle, type PlanningAppearancePreview } from './planningStyle';
import { casurHydricLayerVisibility } from './casurHydricVisibility';
import { BoundaryCutEndpointMarkers } from './BoundaryCutEndpointMarkers';
import { outlineSupersample, SupersampledVectorImageLayer } from './SupersampledVectorImageLayer';

const sameIds = (left: ReadonlySet<string>, right: ReadonlySet<string>): boolean =>
  left.size === right.size && [...left].every((id) => right.has(id));
const NO_IDS: ReadonlySet<string> = new Set();
/**
 * The overlay is repainted on every frame (also while panning), so it is only used for selections that are cheap to draw;
 * larger ones are styled inside the logical layer again (one raster per change, reused while panning), as before.
 */
export const MAX_SELECTION_OVERLAY_ENTITIES = 100;
export const MAX_SELECTION_OVERLAY_VERTICES = 4_000;
const SELECTION_ERASE_STYLE = new Style({ fill: new Fill({ color: '#000' }) });

/** Logical entity ids drawn as selected (SelectionSet entries plus the legacy single selection). */
function selectedEntityIdsFor(state: AppState): Set<string> {
  const ids = new Set(state.selectionSet.entries.filter((entry) => entry.source === 'master' && entry.kind !== 'fragment').map((entry) => entry.id));
  if (state.selection?.kind === 'finca' || state.selection?.kind === 'suerte' || state.selection?.kind === 'tablon') ids.add(state.selection.id);
  if (state.selection?.kind === 'fragment') {
    const fragment = state.model?.fragmentsById.get(state.selection.id);
    if (fragment) ids.add(state.entityViewLevel === 'finca' ? fragment.fincaId : state.entityViewLevel === 'tablon' ? fragment.tablonId : fragment.suerteId);
  }
  return ids;
}

/** Same order the layer would use: all fills (zIndex 18) before all outlines (zIndex 20); stable within a zIndex. */
const byZIndex = <T extends { readonly style: Style }>(items: readonly T[]): T[] => [...items].sort((a, b) => (a.style.getZIndex() ?? 0) - (b.style.getZIndex() ?? 0));

export class MapController {
  readonly map: Map;
  private readonly masterSource = new VectorSource();
  private readonly entityPresentationSource = new VectorSource<Feature<Geometry>>();
  private readonly labelSource = new VectorSource<Feature<Point>>();
  private readonly partialPreviewSource = new VectorSource<Feature<Geometry>>();
  private readonly boundaryCutCandidateSource = new VectorSource<Feature<Geometry>>();
  private readonly boundaryCutEndpointMarkers = new BoundaryCutEndpointMarkers();
  private readonly differenceRemovedSource = new VectorSource<Feature<Geometry>>();
  private readonly differenceResultSource = new VectorSource<Feature<Geometry>>();
  private readonly masterLayer = new VectorLayer({ source: this.masterSource, properties: { role: 'master' } });
  /**
   * Logical entities (Finca/Suerte/Tablón) render through an image layer: during pan/zoom the last image
   * is reused instead of redrawing thousands of polygons per frame; it is redrawn once the view settles.
   */
  // Own canvas (className): the selection overlay erases and repaints selected entities on this layer only.
  private readonly entityPresentationLayer = new SupersampledVectorImageLayer({ source: this.entityPresentationSource, imageRatio: 1.5, className: 'ol-layer ol-entity-presentation', properties: { role: 'entity-presentation' } });
  /**
   * Selected logical entities are painted in the logical layer's postrender instead of re-styling the whole layer:
   * changing the selection then repaints a few features instead of re-rasterizing every visible entity.
   */
  private selectionOverlay: { readonly ids: ReadonlySet<string>; readonly style: (feature: FeatureLike) => Style | Style[] | undefined } | null = null;
  private presentationOptions: Parameters<typeof entityPresentationStyle>[0] | null = null;
  // Features and styles of the current overlay, resolved once per overlay/source revision instead of on every frame.
  private overlayPaint: { readonly overlay: NonNullable<MapController['selectionOverlay']>; readonly revision: number; readonly features: readonly FeatureLike[]; readonly painted: readonly { readonly feature: FeatureLike; readonly style: Style }[] } | null = null;
  private readonly labelLayer = new VectorLayer({ source: this.labelSource, declutter: true, style: labelStyle('cartographic'), properties: { role: 'labels' } });
  private static readonly PARTIAL_PREVIEW_STYLE = new Style({ fill: new Fill({ color: 'rgba(255,138,61,.35)' }), stroke: new Stroke({ color: '#d45113', width: 3, lineDash: [8, 5] }) });
  /** Second geometry of the same preview: what remains of the parent (guided split). */
  private static readonly PARTIAL_REST_STYLE = new Style({ fill: new Fill({ color: 'rgba(43,123,186,.22)' }), stroke: new Stroke({ color: '#1f5f99', width: 2.5, lineDash: [3, 4] }) });
  private readonly partialPreviewLayer = new VectorLayer({ source: this.partialPreviewSource, style: (feature) => feature.get('previewPart') === 'rest' ? MapController.PARTIAL_REST_STYLE : MapController.PARTIAL_PREVIEW_STYLE, properties: { role: 'partial-preview' } });
  private readonly boundaryCutCandidateLayer = new VectorLayer({
    source: this.boundaryCutCandidateSource,
    style: (feature) => new Style({
      fill: new Fill({ color: feature.get('hovered') ? 'rgba(255,138,61,.34)' : 'rgba(45,64,74,.14)' }),
      stroke: new Stroke({ color: feature.get('hovered') ? '#ff7a1a' : '#415965', width: feature.get('hovered') ? 4 : 2, lineDash: [8, 5] }),
    }),
    properties: { role: 'boundary-cut-candidates' },
  });
  private readonly differenceRemovedLayer = new VectorLayer({ source: this.differenceRemovedSource, style: new Style({ fill: new Fill({ color: 'rgba(255,0,168,.42)' }), stroke: new Stroke({ color: '#ff00a8', width: 4, lineDash: [10, 5] }) }), properties: { role: 'difference-removed-preview' } });
  private readonly differenceResultLayer = new VectorLayer({ source: this.differenceResultSource, style: new Style({ fill: new Fill({ color: 'rgba(0,229,255,.12)' }), stroke: new Stroke({ color: '#00e5ff', width: 4 }) }), properties: { role: 'difference-result-preview' } });
  private readonly casurChannelSource = new VectorSource<Feature<Geometry>>();
  private readonly casurDrainageSource = new VectorSource<Feature<Geometry>>();
  private readonly casurChannelLayer = new VectorLayer({ source: this.casurChannelSource, properties: { role: 'casur-reference', casurHydricKind: 'channels' } });
  private readonly casurDrainageLayer = new VectorLayer({ source: this.casurDrainageSource, properties: { role: 'casur-reference', casurHydricKind: 'drainage' } });
  private model: LayerModel | null = null;
  private selection: Selection = null;
  private lastMasterDisplay: { readonly model: LayerModel; readonly ids: ReadonlySet<string>; readonly level: AppState['entityViewLevel']; readonly editKey: string } | null = null;
  private readonly genericLayers = new globalThis.Map<string, VectorLayer<VectorSource>>();
  private interactionStartedAt = 0;
  private lastFrameAt = 0;
  private styleCallbackCount = 0;
  private slowFrameCount = 0;

  constructor(target: HTMLElement, private readonly store: AppStore) {
    const precisionWheelZoom = new PrecisionWheelZoom();
    this.map = new Map({
      target,
      layers: [
        basemapLayers.cartographic,
        basemapLayers.satellite,
        basemapLayers.hybrid,
        this.casurDrainageLayer,
        this.casurChannelLayer,
        this.entityPresentationLayer,
        this.masterLayer,
        this.labelLayer,
        this.partialPreviewLayer,
        this.boundaryCutCandidateLayer,
        this.differenceResultLayer,
        this.differenceRemovedLayer,
        this.boundaryCutEndpointMarkers.layer,
      ],
      view: new View({ center: [-9567000, 1285000], zoom: 8, minZoom: 3, constrainResolution: false }),
      controls: defaultControls({ rotate: false, attribution: true, zoom: false }).extend([
        new PrecisionZoomControl(),
        new ScaleLine({ units: 'metric', bar: true, steps: 3 }),
      ]),
      interactions: defaultInteractions({ mouseWheelZoom: false }).extend([
        new MouseWheelZoom({ condition: (event) => shouldUseNativeWheelZoom(precisionWheelZoom, event.originalEvent as WheelEvent) }),
        precisionWheelZoom,
      ]),
    });
    // V1.9.3: the map starts on what the state says (Híbrido by default), not on a fixed basemap.
    const initial = this.store.getState(); this.setBasemap(initial.presentationWorkspace.showBasemap ? initial.basemap : 'none'); this.setHybridReference(initial.hybridReference);
    this.partialPreviewLayer.setZIndex(10_000);
    this.boundaryCutCandidateLayer.setZIndex(10_005);
    this.differenceResultLayer.setZIndex(10_010);
    this.differenceRemovedLayer.setZIndex(10_020);
    this.boundaryCutEndpointMarkers.layer.setZIndex(10_030);
    this.casurDrainageLayer.setZIndex(70);
    this.casurChannelLayer.setZIndex(80);
    this.entityPresentationLayer.on('postrender', (event) => this.drawSelectionOverlay(event));
    // OpenLayers benchmarks canvas reads (~150 ms, once per page) on its first hit test: pay it while idle, not on the first click.
    const warmHitDetection = this.entityPresentationLayer.on('postrender', () => {
      unByKey(warmHitDetection);
      const whenIdle = 'requestIdleCallback' in window ? (callback: () => void) => window.requestIdleCallback(callback) : (callback: () => void) => window.setTimeout(callback, 300);
      whenIdle(() => this.map.forEachFeatureAtPixel([0, 0], () => undefined, { layerFilter: (layer) => layer === this.entityPresentationLayer }));
    });
    this.map.on('moveend', () => this.setLabels());
    this.map.on('movestart', () => {
      this.interactionStartedAt = performance.now(); this.lastFrameAt = this.interactionStartedAt; this.styleCallbackCount = 0; this.slowFrameCount = 0;
    });
    this.map.on('postrender', () => {
      if (!this.interactionStartedAt) return;
      const now = performance.now(); if (this.lastFrameAt && now - this.lastFrameAt > 34) this.slowFrameCount += 1; this.lastFrameAt = now;
    });
    this.map.on('rendercomplete', () => {
      const targetElement = this.map.getTargetElement();
      if (this.interactionStartedAt) {
        targetElement.dataset.renderMs = (performance.now() - this.interactionStartedAt).toFixed(1);
        targetElement.dataset.slowFrames = String(this.slowFrameCount);
        targetElement.dataset.styleCallbacks = String(this.styleCallbackCount);
        this.interactionStartedAt = 0;
      }
      targetElement.dataset.presentationFeatures = String(this.entityPresentationSource.getFeatures().length);
      targetElement.dataset.physicalFeatures = String(this.masterSource.getFeatures().length);
    });
  }

  setModel(model: LayerModel | null): void {
    this.model = model;
    this.masterSource.clear(true);
    this.entityPresentationSource.clear(true);
    this.lastMasterDisplay = null;
  }

  setMasterDisplay(state: AppState): void {
    if (!state.model) {
      this.masterSource.clear(true); this.entityPresentationSource.clear(true); this.lastMasterDisplay = null; return;
    }
    const ids = deriveDisplaySet(state).masterFragmentIds;
    const editKey = state.geometryEditSession ? `${state.geometryEditSession.entityKind}:${state.geometryEditSession.entityId}` : '';
    const unchanged = this.lastMasterDisplay?.model === state.model
      && this.lastMasterDisplay.level === state.entityViewLevel
      && this.lastMasterDisplay.editKey === editKey
      && sameIds(this.lastMasterDisplay.ids, ids);
    if (unchanged) return;
    this.masterSource.clear(true);
    this.entityPresentationSource.clear(true);
    const physicalIds = state.entityViewLevel === 'fragment'
      ? ids
      : state.geometryEditSession
        ? new Set(entityViewFragmentIds(state.model, state.geometryEditSession.entityKind, state.geometryEditSession.entityId).filter((id) => ids.has(id)))
        : new Set<string>();
    this.masterSource.addFeatures([...physicalIds].flatMap((id) => {
      const feature = state.model?.fragmentsById.get(id)?.feature;
      return feature ? [feature] : [];
    }));
    if (state.entityViewLevel !== 'fragment') this.entityPresentationSource.addFeatures([...buildEntityPresentationFeatures(state.model, state.entityViewLevel, ids)]);
    this.lastMasterDisplay = { model: state.model, ids: new Set(ids), level: state.entityViewLevel, editKey };
  }

  setSelection(selection: Selection): void {
    this.selection = selection;
  }

  setPartialAreaPreview(geometry: Geometry | null, rest: Geometry | null = null): void {
    this.partialPreviewSource.clear(true);
    if (geometry) this.partialPreviewSource.addFeature(new Feature(geometry.clone()));
    if (rest) this.partialPreviewSource.addFeature(new Feature({ geometry: rest.clone(), previewPart: 'rest' }));
  }

  /** Keeps a geometry inside the part of the map not covered by a bottom sheet or a floating card. */
  fitInVisibleArea(extent: readonly number[], covered: { readonly bottom: number; readonly right?: number } = { bottom: 0 }): void {
    const size = this.map.getSize(); if (!size) return;
    const bottom = Math.min(Math.max(0, covered.bottom), (size[1] ?? 0) * 0.7);
    this.map.getView().fit([...extent], { padding: [110, Math.max(40, covered.right ?? 0) + 20, bottom + 40, 40], duration: 300, maxZoom: 18 });
  }

  setDifferencePreview(removed: Geometry | null, result: Geometry | null): void {
    this.differenceRemovedSource.clear(true);
    this.differenceResultSource.clear(true);
    if (removed) this.differenceRemovedSource.addFeature(new Feature(removed.clone()));
    if (result) this.differenceResultSource.addFeature(new Feature(result.clone()));
  }

  setBoundaryCutCandidates(candidates: readonly Polygon[], hoveredIndex: number | null = null): void {
    this.boundaryCutCandidateSource.clear(true);
    this.boundaryCutCandidateSource.addFeatures(candidates.map((geometry, candidateIndex) => new Feature({
      geometry: geometry.clone(), candidateIndex, hovered: candidateIndex === hoveredIndex,
    })));
  }

  setBoundaryCutEndpoints(start: readonly number[] | null, end: readonly number[] | null): void {
    this.boundaryCutEndpointMarkers.set(start, end);
  }

  boundaryCutCandidateAtPixel(pixel: readonly number[]): number | null {
    let candidate: number | null = null;
    this.map.forEachFeatureAtPixel(pixel as [number, number], (feature, layer) => {
      if (layer !== this.boundaryCutCandidateLayer) return undefined;
      const index = feature.get('candidateIndex');
      if (typeof index === 'number') candidate = index;
      return feature;
    }, { layerFilter: (layer) => layer === this.boundaryCutCandidateLayer });
    return candidate;
  }

  /** Object whose geometry is being edited (hidden on its layer while the edit preview draws it). */
  private planningEditTarget: { readonly layerId: string; readonly featureId: string } | null = null;

  setPlanningEditTarget(target: { readonly layerId: string; readonly featureId: string } | null): void {
    this.planningEditTarget = target;
    this.syncLayers(this.store.getState());
  }

  /** «Apariencia» being edited and not applied yet: drawn on the map only (never in the project nor in the PNG). */
  private appearancePreview: PlanningAppearancePreview | null = null;
  private readonly previewSource = (): PlanningAppearancePreview | null => this.appearancePreview;

  /** Repaints only the object's planning layer (no store update, no Maestro redraw): fast enough for every slider step. */
  setPlanningAppearancePreview(preview: PlanningAppearancePreview | null): void {
    const layerId = preview?.layerId ?? this.appearancePreview?.layerId;
    this.appearancePreview = preview;
    if (layerId) this.genericLayers.get(layerId)?.changed();
  }

  /**
   * Draws the whole composition (layers, Maestro presentation, labels). The PNG exporter calls it with its «Etiquetas»
   * option and render scale, then once more with the plain state to restore the screen: `labels` undefined means the
   * on-screen behaviour (Maestro and planning labels follow «Etiquetas» of Presentación).
   */
  renderComposition(state: AppState, labels?: boolean, renderScale = 1): void {
    // The exporter passes `labels`: an unapplied appearance preview is never exported.
    this.syncLayers(state, renderScale, labels ?? state.presentationWorkspace.showLabels, labels === undefined);
    this.setPresentation(state, renderScale);
    this.setLabels(labels);
  }

  /** `planningLabels`: the same «Etiquetas» switch as the Maestro labels (Presentación); the PNG exporter passes its own. */
  syncLayers(state: AppState, renderScale = 1, planningLabels = state.presentationWorkspace.showLabels, appearancePreview = true): void {
    const displaySet = deriveDisplaySet(state);
    const genericIds = new Set(state.layers.filter((layer) => layer.kind !== 'master').map((layer) => layer.id));
    this.genericLayers.forEach((mapLayer, id) => {
      if (!genericIds.has(id)) { this.map.removeLayer(mapLayer); this.genericLayers.delete(id); }
    });
    state.layers.forEach((layer) => {
      const zIndex = 100 + layer.order * 10;
      if (layer.kind === 'master') {
        [this.entityPresentationLayer, this.masterLayer, this.labelLayer].forEach((mapLayer, offset) => {
          mapLayer.setVisible(layer.visible && (mapLayer !== this.masterLayer || state.entityViewLevel === 'fragment' || Boolean(state.geometryEditSession)) && (mapLayer !== this.entityPresentationLayer || state.entityViewLevel !== 'fragment'));
          mapLayer.setOpacity(layer.opacity);
          mapLayer.setZIndex(zIndex + offset);
        });
        return;
      }
      let mapLayer = this.genericLayers.get(layer.id);
      if (!mapLayer) {
        // Planning labels are decluttered against each other and against the symbols, which are never hidden.
        mapLayer = new VectorLayer({ source: new VectorSource(), declutter: layer.planning ? `planning:${layer.id}` : false, properties: { role: 'generic', layerId: layer.id } });
        this.genericLayers.set(layer.id, mapLayer);
        this.map.addLayer(mapLayer);
      }
      const visibleIds = displaySet.layerFeatureIds.get(layer.id) ?? new Set(layer.features.map(({ id }) => id));
      if (mapLayer.get('recordsRef') !== layer.features || mapLayer.get('workScopeRef') !== state.workScope || mapLayer.get('scopeModeRef') !== layer.scopeMode) {
        const source = mapLayer.getSource();
        source?.clear(true);
        source?.addFeatures(layer.features.filter((record) => visibleIds.has(record.id)).map((record) => record.feature));
        mapLayer.set('recordsRef', layer.features, true);
        mapLayer.set('workScopeRef', state.workScope, true);
        mapLayer.set('scopeModeRef', layer.scopeMode, true);
      }
      mapLayer.setVisible(layer.visible);
      mapLayer.setOpacity(layer.opacity);
      mapLayer.setZIndex(zIndex + 5);
      const selectedFeatureIds = new Set(state.selectionSet.layerId === layer.id
        ? state.selectionSet.entries.filter((entry) => entry.source === 'generic').map((entry) => entry.id)
        : state.genericSelection?.layerId === layer.id ? [state.genericSelection.featureId] : []);
      const layerHasThematic = state.classificationConfig.layerId === layer.id && (state.classificationConfig.mode === 'single' || Boolean(state.classificationConfig.field));
      const classificationIds = layerHasThematic ? visibleThemeTargetIds(state, layer) : new Set<string>();
      const thematic = layerHasThematic
        ? { config: state.classificationConfig, categoryByFeatureId: state.classificationConfig.field ? layerClassificationAssignments(layer, state.classificationConfig.field, classificationIds, state.classificationConfig).categoryByFragmentId : new globalThis.Map<string, string>(), subsetIds: classificationIds }
        : undefined;
      const hiddenFeatureId = this.planningEditTarget?.layerId === layer.id ? this.planningEditTarget.featureId : null;
      mapLayer.setStyle(layer.planning ? planningLayerStyle(layer, selectedFeatureIds, renderScale, { showLabels: planningLabels, hiddenFeatureId, ...(appearancePreview ? { preview: this.previewSource } : {}) }) : genericVectorStyle(layer, selectedFeatureIds, thematic, state.subareaLabelsVisible, renderScale, layer.kind === 'subarea' ? exceptionSubareaIds(state) : undefined));
    });
  }

  syncCasurHydricReference(state: AppState): void {
    const reference = state.casurHydricReference;
    if (this.casurChannelLayer.get('referenceRef') !== reference?.channels.features) {
      this.casurChannelSource.clear(true); if (reference) this.casurChannelSource.addFeatures(reference.channels.features.map(({ feature }) => feature));
      this.casurChannelLayer.set('referenceRef', reference?.channels.features ?? null, true);
    }
    if (this.casurDrainageLayer.get('referenceRef') !== reference?.drainage.features) {
      this.casurDrainageSource.clear(true); if (reference) this.casurDrainageSource.addFeatures(reference.drainage.features.map(({ feature }) => feature));
      this.casurDrainageLayer.set('referenceRef', reference?.drainage.features ?? null, true);
    }
    const visibility = casurHydricLayerVisibility(state);
    this.casurChannelLayer.setVisible(visibility.channels);
    this.casurDrainageLayer.setVisible(visibility.drainage);
    this.casurChannelLayer.setStyle(casurHydricStyle('channels', state.casurHydricSelection?.kind === 'channels' ? state.casurHydricSelection.featureId : null));
    this.casurDrainageLayer.setStyle(casurHydricStyle('drainage', state.casurHydricSelection?.kind === 'drainage' ? state.casurHydricSelection.featureId : null));
  }

  setPresentation(state: AppState, renderScale = 1): void {
    const presentationBasemap = state.presentationWorkspace.showBasemap ? state.basemap : 'none';
    const setIds = selectedFragmentIdsForSet(state.model, state.selectionSet);
    const selectedIds = setIds.size ? setIds : selectedFragmentIds(state.model, state.selection);
    const scopedIds = deriveDisplaySet(state).masterFragmentIds;
    const masterClassification = Boolean(state.model && state.classificationConfig.layerId === state.model.id && (state.classificationConfig.mode === 'single' || state.classificationConfig.field));
    const masterLayer = masterClassification ? state.layers.find(({ id }) => id === state.model!.id) : undefined;
    const theme = masterLayer ? masterThemeAssignments(state, masterLayer) : null;
    const classificationIds = theme?.subsetIds ?? (masterLayer ? visibleThemeTargetIds(state, masterLayer) : new Set<string>());
    // V1.8.1: with an active secondary classification, the principal one only fills (B owns the boundary); its stored
    // paint is untouched, so turning B off restores the previous style exactly.
    const dual = masterClassification ? secondaryTheme(state) : null;
    const masterConfig = masterClassification ? { ...state.classificationConfig, level: state.entityViewLevel, ...(dual ? { paint: 'fill' as const } : {}) } : { ...state.classificationConfig, field: null, categories: [] };
    const secondaryWidth = (dual?.config.width ?? 0) * renderScale;
    // V1.9.0 E4: thin B outlines on a low-density screen are rendered supersampled (sharp colour and casing).
    this.entityPresentationLayer.setSupersample(outlineSupersample(dual && state.entityViewLevel !== 'fragment' ? secondaryWidth : null, this.map.getPixelRatio?.() ?? window.devicePixelRatio ?? 1));
    const secondaryLine = (categoryById: ReadonlyMap<string, string>) => dual ? { categoryById, categories: dual.categories, width: secondaryWidth, ...(dual.config.line === 'dashed' ? { lineDash: [secondaryWidth * 2.6, secondaryWidth * 1.7] } : {}) } : undefined;
    const assignments = theme?.assignments
      ?? { categoryByFragmentId: new globalThis.Map<string, string>(), categoryByEntityId: new globalThis.Map<string, string>(), hasValueByFragmentId: new globalThis.Map<string, boolean>(), hasValueByEntityId: new globalThis.Map<string, boolean>() };
    const selectedEntityIds = selectedEntityIdsFor(state);
    const entityStyle = state.entityViewLevel === 'fragment' ? { ...state.styleConfig, showPhysicalFragments: true } : state.styleConfig;
    const scaledStyle = renderScale === 1 ? entityStyle : { ...entityStyle, outlineWidth: entityStyle.outlineWidth * renderScale, selectionOutlineWidth: entityStyle.selectionOutlineWidth * renderScale };
    const common = { scopedIds, selectedEntityIds, remainderVisibility: state.remainderVisibility, basemap: presentationBasemap, styleConfig: scaledStyle, classificationConfig: masterConfig, categoryByEntityId: assignments.categoryByEntityId, hasValueByEntityId: assignments.hasValueByEntityId, classificationSubsetIds: classificationIds, piecesByEntityId: theme?.mixed || theme?.scenario ? theme.piecesBySuerteId : undefined, editingEntityKind: state.geometryEditSession?.entityKind, editingEntityId: state.geometryEditSession?.entityId, secondary: secondaryLine(dual?.assignments.categoryByEntityId ?? new globalThis.Map()) };
    const physicalStyle = fragmentStyle({
      selectedIds,
      scopedIds,
      remainderVisibility: state.remainderVisibility,
      basemap: presentationBasemap,
      styleConfig: scaledStyle,
      classificationConfig: masterConfig,
      categoryByFragmentId: assignments.categoryByFragmentId,
      hasValueByFragmentId: assignments.hasValueByFragmentId,
      classificationSubsetIds: classificationIds,
      editingFragmentId: state.geometryEditSession?.selectedFragmentId,
      ...(dual && state.entityViewLevel === 'fragment' ? { secondary: secondaryLine(dual.assignments.categoryByFragmentId) } : {}),
    });
    // Overlay only on screen, for logical levels outside geometry editing (editing keeps its own zIndex interplay).
    const overlay = renderScale === 1 && this.overlayFits(state, selectedEntityIds);
    const logicalStyle = entityPresentationStyle(overlay ? { ...common, selectedEntityIds: NO_IDS } : common);
    this.presentationOptions = overlay ? common : null;
    this.selectionOverlay = overlay && selectedEntityIds.size ? { ids: selectedEntityIds, style: entityPresentationStyle(common) } : null;
    this.masterLayer.setStyle((feature) => { this.styleCallbackCount += 1; return physicalStyle(feature); });
    this.entityPresentationLayer.setStyle((feature) => { this.styleCallbackCount += 1; return logicalStyle(feature); });
    this.labelLayer.setStyle(labelStyle(presentationBasemap, renderScale, state.labelConfig));
  }

  /**
   * Selection-only change (nothing else in the presentation changed since the last setPresentation): repaint just the
   * overlay when it is active; otherwise fall back to the full presentation, exactly as before.
   */
  updateSelection(state: AppState): void {
    const options = this.presentationOptions;
    const selectedEntityIds = selectedEntityIdsFor(state);
    if (!options || !this.overlayFits(state, selectedEntityIds)) { this.setPresentation(state); return; }
    const selected = { ...options, selectedEntityIds };
    this.presentationOptions = selected;
    this.selectionOverlay = selectedEntityIds.size ? { ids: selectedEntityIds, style: entityPresentationStyle(selected) } : null;
    this.map.render();
  }

  private overlayFits(state: AppState, selectedEntityIds: ReadonlySet<string>): boolean {
    // VectorImage emits its on-screen postrender only in the deferred (declutter) pass, which the label layer enables.
    if (!this.labelLayer.getDeclutter() || state.entityViewLevel === 'fragment' || state.geometryEditSession || selectedEntityIds.size > MAX_SELECTION_OVERLAY_ENTITIES) return false;
    let vertices = 0;
    for (const feature of this.entityPresentationSource.getFeatures()) {
      if (selectedEntityIds.has(String(feature.get('entityId')))) vertices += ((feature.getGeometry() as SimpleGeometry | undefined)?.getFlatCoordinates().length ?? 0) / 2;
      if (vertices > MAX_SELECTION_OVERLAY_VERTICES) return false;
    }
    return true;
  }

  /** Erases the selected entities from this layer's own canvas and repaints them with the selected style. */
  private drawSelectionOverlay(event: RenderEvent): void {
    const overlay = this.selectionOverlay;
    if (!overlay || !this.entityPresentationLayer.getVisible()) return;
    // The layer's inner vector renderer also emits postrender while drawing its offscreen image: only paint on screen.
    if (event.context !== (this.entityPresentationLayer.getRenderer() as unknown as { readonly context?: unknown } | null)?.context) return;
    const revision = this.entityPresentationSource.getRevision();
    if (this.overlayPaint?.overlay !== overlay || this.overlayPaint.revision !== revision) {
      const selected = this.entityPresentationSource.getFeatures().filter((feature) => overlay.ids.has(String(feature.get('entityId'))));
      const painted = byZIndex(selected.flatMap((feature) => { const styles = overlay.style(feature); this.styleCallbackCount += 1; return styles ? (Array.isArray(styles) ? styles : [styles]).map((style) => ({ feature, style })) : []; }));
      this.overlayPaint = { overlay, revision, features: [...new Set(painted.map(({ feature }) => feature))], painted };
    }
    const { features, painted } = this.overlayPaint;
    if (!painted.length) return;
    const context = event.context as CanvasRenderingContext2D; const vectorContext = getVectorContext(event);
    context.save();
    context.globalCompositeOperation = 'destination-out';
    features.forEach((feature) => vectorContext.drawFeature(feature as Feature, SELECTION_ERASE_STYLE));
    context.restore();
    painted.forEach(({ feature, style }) => vectorContext.drawFeature(feature as Feature, style));
  }

  setLabels(enabled = this.store.getState().presentationWorkspace.showLabels): void {
    this.labelSource.clear(true);
    const state = this.store.getState();
    const size = this.map.getSize();
    if (!state.model || !size) return;
    this.labelSource.addFeatures(buildLabelFeatures(state.model, enabled ? state.labelMode : 'none', state.selection, {
      viewScope: state.viewScope,
      activeFincaId: state.activeFincaId,
      labelConfig: state.labelConfig,
      zoom: this.map.getView().getZoom(),
      visibleExtent: this.map.getView().calculateExtent(size),
      operationalFields: state.operationalFields,
      workScope: state.workScope,
      allowedFragmentIds: labelTargetFragmentIds(state),
      labelTargetSource: state.labelConfig.labelTarget.source,
    }));
  }

  setBasemap(id: BasemapId): void {
    setVisibleBasemap(id);
    this.map.getTargetElement().classList.toggle('is-satellite', id === 'satellite' || id === 'hybrid');
  }

  zoomToSelection(): void {
    const state = this.store.getState();
    const hydric = state.casurHydricSelection;
    if (hydric && state.casurHydricReference) {
      const layer = hydric.kind === 'channels' ? state.casurHydricReference.channels : state.casurHydricReference.drainage;
      const extent = layer.featuresById.get(hydric.featureId)?.feature.getGeometry()?.getExtent();
      if (extent) this.map.getView().fit(extent, { padding: [90, 110, 120, 110], duration: 350, maxZoom: 18 });
      return;
    }
    const generic = state.genericSelection;
    if (generic) {
      const feature = this.store.getState().layers.find((layer) => layer.id === generic.layerId)?.featuresById.get(generic.featureId)?.feature;
      const extent = feature?.getGeometry()?.getExtent();
      if (extent) this.map.getView().fit(extent, { padding: [90, 110, 120, 110], duration: 350, maxZoom: 18 });
      return;
    }
    this.fitFragmentIds(selectedFragmentIds(this.model, this.selection));
  }

  /** Fits the Maestro entities visible now (DisplaySet: WorkScope + filters + active geometry). False when none is visible. */
  zoomToScope(): boolean {
    const state = this.store.getState();
    return this.fitFragmentIds(deriveDisplaySet(state).masterFragmentIds);
  }

  zoomToLayer(layerId = this.store.getState().activeLayerId): boolean {
    const layer = this.store.getState().layers.find((item) => item.id === layerId);
    if (layer?.kind === 'master') return this.zoomToScope();
    const extent = layer?.extent;
    if (extent) this.map.getView().fit([...extent], { padding: [70, 80, 90, 80], duration: 350, maxZoom: 17 });
    return Boolean(extent);
  }

  updateSize(): void {
    this.map.updateSize();
  }

  visibleMasterFeatureCount(): number {
    return this.entityPresentationSource.getFeatures().length || this.masterSource.getFeatures().length;
  }

  presentationFeatureCount(): number { return this.entityPresentationSource.getFeatures().length; }
  physicalFeatureCount(): number { return this.masterSource.getFeatures().length; }

  setHybridReference(visibility: HybridReferenceVisibility): void {
    setHybridReferenceVisibility(visibility);
  }

  refreshBasemaps(): void {
    refreshBasemapLayers();
  }

  getLabelLayer(): VectorLayer<VectorSource<Feature<Point>>> { return this.labelLayer; }

  private fitFragmentIds(ids: ReadonlySet<string>): boolean {
    const features = [...ids].flatMap((id) => {
      const feature = this.model?.fragmentsById.get(id)?.feature;
      return feature ? [feature] : [];
    });
    if (!features.length) return false;
    const source = new VectorSource({ features });
    const extent = source.getExtent();
    if (!extent || !extent.every(Number.isFinite)) return false;
    this.map.getView().fit(extent, { padding: [90, 110, 120, 110], duration: 350, maxZoom: 18 });
    return true;
  }
}
